import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import { STORAGE } from '../../constants';
import { LocalStorageService } from '../../services/local-storage.service';
import { SessionService } from '../../services/session.service';
import { AlertService } from '../../services/alert.service';
import { useDrawerCleanup } from '../../hooks/use-drawer-cleanup.hook';
import { usePermissions } from '../../hooks/use-permissions.hook';
import { LandingRouteService } from '../../services/landing-route.service';
import { MODULE_HEADER_MAP, ModuleHeaderConfig } from './header-actions-config.component';
import Tooltip from '../forms/tooltip.component';
import Icon from '../../components/forms/icon.component';
import DropTheme from './drop-theme.component';
import SettingsModal from '../../pages/raptor/settings/modal/settings.modal'; // Asegura colocar la ruta correcta

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [session, setSession] = useState(() => LocalStorageService.get(STORAGE.SESSION));
  const { canView } = usePermissions();
  const { cleanupOverlays } = useDrawerCleanup();
  const [isFormValid, setIsFormValid] = useState<boolean>(false);

  const homeRoute = canView('dashboard')
    ? LandingRouteService.getDashboardRoute()
    : LandingRouteService.getWelcomeRoute();

  const getCurrentModule = (): ModuleHeaderConfig | null => {
    const match = Object.keys(MODULE_HEADER_MAP).find((route) =>
      location.pathname.startsWith(route)
    );
    return match ? MODULE_HEADER_MAP[match] : null;
  };

  const currentModule = getCurrentModule();

  const [showModal, setShowModal] = useState<{
    show: boolean;
    data: null;
  }>({
    show: false,
    data: null
  });

  const handleShowModal = async () => {
    setShowModal({ show: false, data: null });
  };

  const refreshSession = useCallback(() => {
    setSession(LocalStorageService.get(STORAGE.SESSION));
  }, []);

  useEffect(() => {
    cleanupOverlays();
  }, [location.pathname, cleanupOverlays]);

  useEffect(() => {
    const handleValidationChange = (event: Event) => {
      const customEvent = event as CustomEvent<{ isValid: boolean }>;
      setIsFormValid(customEvent.detail.isValid);
    };

    window.addEventListener('submit:validation-change', handleValidationChange);
    return () => {
      window.removeEventListener('submit:validation-change', handleValidationChange);
    };
  }, []);

  const logout = useCallback(() => {
    // Revoca el refresh en el backend (best-effort), limpia storage y redirige al login.
    void SessionService.logout();
  }, []);

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === STORAGE.SESSION) {
        const newSession = LocalStorageService.get(STORAGE.SESSION);
        setSession(newSession);

        if (!newSession) {
          navigate('/auth/login');
          AlertService.toastWarning('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.');
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [navigate]);

  const handleCancelAction = async (config: ModuleHeaderConfig['actions']) => {
    if (config?.requiresCancelConfirm) {
      const isConfirmed = await AlertService.question(
        config.confirmTitle || '¿Estás seguro?',
        config.confirmMessage || '¿Deseas salir?',
        () => Promise.resolve(true),
        'Continuar'
      );
      if (!isConfirmed) return;
    }
    navigate(homeRoute);
  };

  return (
    <>
      <div
        id="kt_header"
        className="header"
        data-kt-sticky="true"
        data-kt-sticky-name="header"
        data-kt-sticky-offset="{default: '200px', lg: '300px'}"
      >
        <div className="container-fluid d-flex align-items-stretch justify-content-between">
          <div className="d-flex align-items-center flex-grow-1 flex-lg-grow-0">
            <div className="d-flex align-items-center d-lg-none">
              <div
                className="btn btn-icon btn-active-color-primary ms-n2 me-1"
                id="kt_aside_toggle"
              >
                <Icon icon="ki-abstract-14" size="fs-1" />
              </div>
            </div>
            <Link to={homeRoute} className="d-lg-none">
              <img alt="Logo" src="/assets/media/logos/logo-c.svg" className="mh-40px" />
            </Link>
            <div className="d-flex align-items-center">
              <div
                className="btn btn-icon w-auto ps-0 btn-active-color-primary d-none d-lg-inline-flex me-3 active"
                data-kt-toggle="true"
                data-kt-toggle-state="active"
                data-kt-toggle-target="body"
                data-kt-toggle-name="aside-minimize"
              >
                <Icon icon="ki-black-left-line" size="fs-2x" className="fw-bolder rotate-180" />
              </div>

              {currentModule && (
                <div className="d-flex flex-column justify-content-center ms-5 min-w-0">
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <span className="fw-bolder fs-1 text-primary">{currentModule.titleKey}</span>
                    {currentModule.descriptionSessionKey &&
                      sessionStorage.getItem(currentModule.descriptionSessionKey) && (
                        <span className="badge badge-light-primary fw-semibold px-3 py-2 d-inline-flex align-items-center gap-1 lh-1">
                          <Icon icon="ki-briefcase" size="fs-8" type="primary" />
                          {sessionStorage.getItem(currentModule.descriptionSessionKey)}
                        </span>
                      )}
                  </div>
                  <span className="fs-7 fw-bolder d-none d-md-block">
                    {currentModule.descriptionKey}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="d-flex align-items-stretch justify-content-between z-index-5">
            {currentModule?.actions?.showButtons && (
              <div className="d-flex align-items-center gap-3 me-4">
                <button
                  type="button"
                  className="btn btn-sm btn-dark fw-bolder rounded-12 px-4"
                  onClick={() => handleCancelAction(currentModule.actions)}
                >
                  {currentModule.actions.cancelText || 'Cancelar'}
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-primary fw-bolder rounded-12 px-4"
                  disabled={!isFormValid}
                  onClick={() => {
                    if (currentModule.actions?.customSubmitEvent) {
                      window.dispatchEvent(
                        new CustomEvent(currentModule.actions.customSubmitEvent)
                      );
                    }
                  }}
                >
                  {currentModule.actions.submitText || 'Guardar'}
                </button>
              </div>
            )}

            {currentModule?.backRoute && (
              <Link
                to={currentModule.backRoute}
                className="btn btn-sm btn-light-primary text-primary text-hover-white d-none d-md-inline-flex align-items-center align-self-center gap-2 fw-semibold me-2 me-lg-3 px-3 py-2 rounded-pill"
                aria-label="Regresar"
              >
                <Icon icon="ki-arrow-left" size="fs-5" />
                <span>Regresar</span>
              </Link>
            )}
            <div className="d-flex align-items-center ms-2 ms-lg-3" id="kt_header_user_menu_toggle">
              <div
                className="d-inline-flex align-items-center bg-light-primary rounded-pill px-2 py-1 cursor-pointer"
                data-kt-menu-trigger="{default: 'hover', lg: 'hover'}"
                data-kt-menu-attach="parent"
                data-kt-menu-placement="bottom-end"
              >
                <div className="d-flex align-items-center justify-content-center rounded-circle me-2 symbolHeader">
                  <img
                    src="/assets/media/avatars/av1.png"
                    alt="profile-picture"
                    className="rounded-circle me-2 imgHeader"
                  />
                </div>
                <span
                  className="mx-0 fw-bold fs-6 me-3 text-truncate d-inline-block"
                  style={{ maxWidth: 'clamp(60px, 12vw, 180px)' }}
                >
                  {session?.user?.name || 'Sin información'}
                </span>
              </div>
              <div
                className="menu menu-sub menu-sub-dropdown menu-column b-radius menu-rounded menu-gray-800 fw-semibold py-0 fs-6 w-300px dropdown-topmost"
                data-kt-menu="true"
              >
                <div className="d-flex align-items-center gap-4 bg-light b-radius p-4">
                  <div className="symbol symbol-60px symbol-circle flex-shrink-0">
                    <img src="/assets/media/avatars/av1.png" alt="avatar" />
                  </div>
                  <div className="d-flex flex-column min-w-0">
                    <span
                      className="fw-bold fs-3 text-gray-900 text-truncate"
                      style={{ maxWidth: 200 }}
                    >
                      {session?.user?.username || 'usuario'}
                    </span>
                    <Tooltip title={session?.user?.email ?? 'Correo no registrado'}>
                      <span
                        className="text-gray-600 fs-6 text-truncate"
                        style={{ maxWidth: 200, display: 'inline-block' }}
                      >
                        {session?.user?.email ?? 'Correo no registrado'}
                      </span>
                    </Tooltip>
                    <span
                      className="badge badge-light-primary mt-2 fw-semibold"
                      style={{ width: 'fit-content' }}
                    >
                      {session?.user?.roles[0] === 'superadmin' && 'Super Administrador'}
                      {session?.user?.roles[0] === 'admin' && 'Administrador'}
                      {session?.user?.roles[0] === 'supervisor' && 'Supervisor'}
                      {session?.user?.roles[0] === 'operador' && 'Operador'}
                    </span>
                  </div>
                </div>
                <div className="py-3">
                  <div
                    className="menu-item px-5"
                    onClick={() => setShowModal({ show: true, data: null })}
                  >
                    <span className="menu-link px-0 b-radius">
                      <span className="menu-icon">
                        <Icon icon="ki-setting-2" size="fs-2" />
                      </span>
                      <span className="menu-title">Configuración</span>
                    </span>
                  </div>
                  <DropTheme />
                  <div className={`menu-item px-5 mb-4`} onClick={logout}>
                    <span className="menu-link menu-link-danger px-0 b-radius">
                      <span className="menu-icon">
                        <Icon icon="ki-exit-right" size="fs-2" />
                      </span>
                      <span className="menu-title">Cerrar sesión</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <SettingsModal
        show={showModal.show}
        onClose={() => setShowModal({ show: false, data: null })}
        userSession={session}
        onSessionUpdate={refreshSession}
      />
    </>
  );
};

export default Header;
